import React, { useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekButton, PeekStack, PeekTypography } from "@piiqtechnologies/ui-components";
import { buildOrgPath, PATHS } from "@constants/Paths";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { SeoHead } from "@components/common/seo-head/SeoHead";
import { useOptionalActiveOrganization } from "@stores/ActiveOrganizationContext";
import { useAuthContext } from "@stores/AuthContext";
import { getIsAdmin, getRedirectPathAfterLogin } from "@stores/utils";
import messages from "./messages.i18n";
export const Restricted = () => {
    var _a;
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const track = useTrackEvent();
    const { user } = useAuthContext();
    const isAdmin = getIsAdmin(user);
    const activeOrgContext = useOptionalActiveOrganization();
    // Where "back" goes, and whether there is anywhere to go: the organization being viewed, then the
    // admin dashboard for staff, then the member's own portfolio (or the picker when they have several
    // organizations, or theirs couldn't be loaded). A member with no organization has nowhere to go back to.
    const back = (() => {
        if (activeOrgContext === null || activeOrgContext === void 0 ? void 0 : activeOrgContext.activeOrg) {
            return { to: buildOrgPath(activeOrgContext.activeOrgId, PATHS.HOME), label: messages.backToPortfolio };
        }
        if (isAdmin)
            return { to: PATHS.ADMIN_DASHBOARD, label: messages.backToAdminDashboard };
        const portfolio = getRedirectPathAfterLogin(user);
        return portfolio === PATHS.RESTRICTED ? null : { to: portfolio, label: messages.backToPortfolio };
    })();
    const { communityId, code } = (_a = location.state) !== null && _a !== void 0 ? _a : {};
    const hasTrackedRef = useRef(false);
    useEffect(() => {
        if (hasTrackedRef.current)
            return;
        hasTrackedRef.current = true;
        track(AnalyticsArea.Community, "Restricted Hit", { communityId, code });
    }, [track, communityId, code]);
    return (React.createElement(PeekStack, { "data-testid": "restricted-screen", height: "100%", gap: 2, alignItems: "center", justifyContent: "center", px: 2, sx: { textAlign: "center" } },
        React.createElement(SeoHead, { title: intl.formatMessage(messages.seoTitle), description: intl.formatMessage(messages.seoDescription), canonicalPath: "/restricted" }),
        React.createElement(PeekBox, { sx: {
                width: 64,
                height: 64,
                borderRadius: "50%",
                backgroundColor: "grey.100",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            } },
            React.createElement(PeekBox, { sx: { display: "inline-flex", color: "grey.500" } },
                React.createElement(Lock, { width: 26, height: 26 }))),
        React.createElement(PeekStack, { gap: 1, alignItems: "center" },
            React.createElement(PeekTypography, { variant: "h2" }, intl.formatMessage(messages.title)),
            React.createElement(PeekTypography, { variant: "body1", color: "grey.500" }, intl.formatMessage(messages.description))),
        back && (React.createElement(PeekButton, { size: "large", variant: "contained", onClick: () => navigate(back.to) }, intl.formatMessage(back.label)))));
};
export default Restricted;
